/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useContext, useMemo } from "react";
import styled from "@emotion/styled";
import { NavLink, useNavigate } from "react-router-dom";

import {
  Box,
  Drawer as MuiDrawer,
  ListItemButton,
  FormControl,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatabaseIcon } from "lucide-react";

import { rgba } from "polished";

import { SidebarItemsType } from "../../types/sidebar";
import { MiraTheme } from "../../theme/theme";
import { SidebarNav } from "./SidebarNav";
import { SidebarFooter } from "./SidebarFooter";
import { CirromLogo, CirromLogoContrast, CirromLogoMin } from "../../../Logo";
import { useTheme } from "../../theme/useTheme";
import { THEMES } from "../../theme/constants";
import { SideBarWidthContext } from "../../../../App";
import { IntegratedSystem } from "../../../../api/integratedsystems";
import { DataProject } from "../../../../api/dataprojects";
import { isDemoEnvironment } from "../../../utils/utils";

const createDemoSystem = (
  id: number,
  name: string,
  systemType: string
): IntegratedSystem =>
  ({
    id,
    name,
    system_type: systemType,
    type: [],
  } as IntegratedSystem);

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
    scrollbar-width: none;
  }
`;

const Brand = styled(ListItemButton)<{
  component?: React.ReactNode;
  to?: string;
  theme?: MiraTheme;
}>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${(props) => props.theme.sidebar.header.background};
  }
`;

const DataSourceSelector = styled(Box)<{ theme?: MiraTheme }>`
  background-color: ${(props) => props.theme.sidebar.background};
`;

const DataSourceLabel = styled(Box)<{ theme?: MiraTheme }>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(4)};
  color: ${(props) => props.theme.sidebar.color};
  padding-top: ${(props) => props.theme.spacing(3)};
  padding-bottom: ${(props) => props.theme.spacing(3)};
  padding-left: ${(props) => props.theme.spacing(8)};
  padding-right: ${(props) => props.theme.spacing(7)};
  font-size: ${(props) => props.theme.typography.body1.fontSize}px;
`;

const DataSourceDropdownWrapper = styled(Box)<{ theme?: MiraTheme }>`
  padding-left: ${(props) => props.theme.spacing(14)};
  padding-right: ${(props) => props.theme.spacing(4)};
  padding-bottom: ${(props) => props.theme.spacing(2)};
`;

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  onClose?: () => void;
  items: {
    title: string;
    pages: SidebarItemsType[];
  }[];
  showFooter?: boolean;
  currentUser?: string;
  integratedSystems?: IntegratedSystem[];
  selectedSystemId?: number;
  setSelectedSystemId?: (id: number) => void;
  isDataProject?: boolean;
  currentDataProject?: DataProject;
};

export const CirromSidebar: React.FC<SidebarProps> = ({
  items,
  showFooter = true,
  currentUser,
  integratedSystems,
  selectedSystemId,
  setSelectedSystemId,
  isDataProject,
  currentDataProject,
  ...rest
}) => {
  const theme = useTheme();
  const sideBarWidth = useContext(SideBarWidthContext);
  const navigate = useNavigate();

  // Determine if we're in mobile mode based on variant prop
  const isMobile = rest.variant === "temporary";

  const selectedSystem = integratedSystems?.find(
    (system) => system.id === selectedSystemId
  );

  // Find the INTERACTION system ID for demo redirects
  const interactionSystemId = useMemo(() => {
    return integratedSystems?.find(
      (s) => s.system_type?.toUpperCase() === "INTERACTION"
    )?.id;
  }, [integratedSystems]);

  // Demo entry IDs use negative numbers to avoid conflicts with real systems
  const demoEntries = useMemo(
    () => [
      { name: "ContactEase", systemType: "CONTACTEASE", demoId: -1 },
      { name: "JDSupra", systemType: "JDSUPRA", demoId: -2 },
      { name: "Peppermint", systemType: "PEPPERMINT_CX", demoId: -3 },
      { name: "DealCloud", systemType: "DEAL_CLOUD", demoId: -4 },
      { name: "CVent", systemType: "CVENT", demoId: -5 },
      { name: "Ticketnology", systemType: "TICKETNOLOGY", demoId: -6 },
    ],
    []
  );

  // Build dropdown options with demo entries appended at bottom
  const dropdownOptions = useMemo(() => {
    if (!integratedSystems) return [];

    const existingSystemTypes = integratedSystems.map((s) =>
      s.system_type?.toUpperCase()
    );

    const demoSystems: IntegratedSystem[] = [];
    if (isDemoEnvironment() && interactionSystemId) {
      demoEntries.forEach(({ name, systemType, demoId }) => {
        if (!existingSystemTypes.includes(systemType)) {
          demoSystems.push(createDemoSystem(demoId, name, systemType));
        }
      });
    }
    return [...integratedSystems, ...demoSystems];
  }, [integratedSystems, interactionSystemId, demoEntries]);

  const handleSystemChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSystemId = Number(event.target.value);
    // Demo entries have negative IDs - redirect to the real system
    const actualSystemId = newSystemId < 0 ? interactionSystemId : newSystemId;
    setSelectedSystemId?.(actualSystemId);
    navigate(`/product/dashboard/${actualSystemId}`);
  };

  const dataSourceDropdown = dropdownOptions.length > 0 && (
    <DataSourceSelector>
      <DataSourceLabel>
        <DatabaseIcon size={20} style={{ opacity: 0.5 }} />
        Data Source
      </DataSourceLabel>
      <DataSourceDropdownWrapper>
        <FormControl fullWidth size="small">
          <TextField
            value={selectedSystemId ?? ""}
            select
            onChange={handleSystemChange}
            variant="outlined"
            size="small"
            sx={(muiTheme) => {
              const sidebarTheme = muiTheme as MiraTheme;
              return {
                "& .MuiOutlinedInput-root": {
                  color: sidebarTheme.sidebar.color,
                  backgroundColor: rgba(sidebarTheme.sidebar.color, 0.06),
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: rgba(sidebarTheme.sidebar.color, 0.2),
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
                "& .MuiSelect-select": {
                  py: 1,
                },
                "& .MuiSvgIcon-root": {
                  color: sidebarTheme.sidebar.color,
                },
              };
            }}
          >
            {dropdownOptions.map((system) => (
              <MenuItem key={`${system.id}-${system.name}`} value={system.id}>
                {system.name}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      </DataSourceDropdownWrapper>
    </DataSourceSelector>
  );

  // Data source icon for collapsed sidebar
  const collapsedDataSourceIcon = integratedSystems &&
    integratedSystems.length > 0 && (
      <Tooltip title={selectedSystem?.name || "Data Source"} placement="right">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 2,
            cursor: "pointer",
            //@ts-ignore
            backgroundColor: (theme) => theme.sidebar.background,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
            },
          }}
          onClick={() => {
            if (selectedSystemId) {
              navigate(`/product/dashboard/${selectedSystemId}`);
            }
          }}
        >
          <DatabaseIcon size={20} style={{ opacity: 0.5 }} />
        </Box>
      </Tooltip>
    );

  // Full width sidebar content
  const fullSidebarContent = (
    <>
      <Brand component={NavLink as any} to="/">
        <Box ml={1}>
          {theme.theme === THEMES.CIRROM ? (
            <CirromLogo />
          ) : (
            <CirromLogoContrast />
          )}
        </Box>
      </Brand>
      {dataSourceDropdown}
      <SidebarNav items={items} isCollapsed={false} />
      {!!showFooter && (
        <SidebarFooter currentUser={currentUser} isCollapsed={false} />
      )}
    </>
  );

  // Collapsed sidebar content
  const collapsedSidebarContent = (
    <>
      <Brand component={NavLink as any} to="/" style={{ padding: "16px 12px" }}>
        <Box>
          <CirromLogoMin />
        </Box>
      </Brand>
      {collapsedDataSourceIcon}
      <SidebarNav items={items} isCollapsed={true} />
      {!!showFooter && (
        <SidebarFooter currentUser={currentUser} isCollapsed={true} />
      )}
    </>
  );

  // Mobile drawer (temporary, always full width)
  if (isMobile) {
    return <Drawer {...rest}>{fullSidebarContent}</Drawer>;
  }

  // Desktop: render both collapsed and expanded drawers
  return (
    <>
      {/* Full width drawer */}
      <Drawer
        variant="persistent"
        open={sideBarWidth.value === "full"}
        PaperProps={{
          style: { width: rest.PaperProps?.style?.width || 258 },
        }}
      >
        {fullSidebarContent}
      </Drawer>

      {/* Collapsed drawer */}
      <Drawer
        variant="persistent"
        open={sideBarWidth.value === "small"}
        PaperProps={{
          style: { width: 64 },
        }}
      >
        {collapsedSidebarContent}
      </Drawer>
    </>
  );
};
